/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useMutation } from "@apollo/client";
import { useEffect, useState, useCallback } from "react";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { instantiatePrimaryUser } from "config/ResetState";
import { PrimaryJoinOrgMutation } from "graphql/internal/generated";
import { PRIMARY_JOIN_ORG } from "graphql/internal/Mutations";
import { SkalePrimaryApolloClient } from "config/UpduoApolloClient";

import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function useOrgRoute(history: { push: (path: string) => void }) {
  const authToken = useSelector((state: RootState) => state.authToken);

  const [submitButtonText, setSubmitButtonText] = useState("Submit");
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [orgCode, setOrgCode] = useState("");
  const [orgCodeValidationText, setOrgCodeValidationText] = useState("");
  const [, setRouteHasRendered] = useState(false);

  const [primaryJoinOrg, {}] = useMutation<PrimaryJoinOrgMutation>(
    PRIMARY_JOIN_ORG,
    {
      fetchPolicy: "no-cache", // this line is needed in order to proceed
      client: SkalePrimaryApolloClient,
      onCompleted: ({ primaryJoinOrg: response }) => {
        if (!response?.ok) {
          let newOrgCodeValidationText = "A problem occurred.";
          switch (response?.reason) {
            case "412":
              // Primary user is already a member
              newOrgCodeValidationText = "You are already a member.";
              break;
            case "417":
              // Primary user was a member, but left and is attempting to rejoin
              newOrgCodeValidationText =
                "Your membership to this community was deactivated. To rejoin, please contact your community leader.";
              break;
            case "405":
              // Not allowed to join organization with domain as org code
              newOrgCodeValidationText =
                "Access to this community is not allowed.";
              break;
            case "406":
              // No organization exists with this org code
              newOrgCodeValidationText = "No community found!";
              break;
            case "403":
              // No organization exists with this org code
              newOrgCodeValidationText =
                "Create a new Upduo account using your employee ID and an approved email address.";
              break;
            default:
              newOrgCodeValidationText = "An unknown error ocurred.";
          }
          setOrgCodeValidationText(newOrgCodeValidationText);
          setSubmitButtonDisabled(!orgCode);
          setSubmitButtonText("Submit");
          return;
        } else {
          instantiatePrimaryUser({
            failureCallback: () => {
              setSubmitButtonDisabled(!orgCode);
              setSubmitButtonText("Submit");
            },
            successCallback: () => {
              history.push("/");
            },
            noOrgCallback: () => {
              history.push("/join_organization");
            },
            orgCode,
          });
        }
      },
      onError: (error) => {
        UpduoAnalytics.logEvent("app-joinorganization-primaryjoinorg-error", {
          error,
        });
        console.error("ERROR primaryJoinOrg", error);
      },
    }
  );

  useEffect(() => {
    // Disable button if fields are not validated
    setSubmitButtonDisabled(!orgCode);
  }, [orgCode]);

  useEffect(() => {
    setRouteHasRendered((previousState) => {
      if (previousState) {
        history.push("/");
      }
      return true;
    });
  }, [authToken, history]);

  const _handleButtonPress = useCallback(() => {
    // Post request to join org via org code
    UpduoAnalytics.logEvent("user-org-code-submit", {
      org_code: orgCode,
    });
    setSubmitButtonDisabled(true);
    setSubmitButtonText("Joining...");
    primaryJoinOrg({
      variables: {
        primaryJoinOrgRequest: {
          orgCode,
        },
      },
    });
  }, [orgCode, history]);

  return {
    authToken,
    submitButtonText,
    submitButtonDisabled,
    orgCode,
    setOrgCode,
    orgCodeValidationText,
    setOrgCodeValidationText,
    _handleButtonPress,
  };
}
